import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoEyeOutline,
  IoCreateOutline,
  IoTrashOutline,
  IoPrintOutline,
  IoSendOutline,
  IoCheckmarkOutline,
  IoCloseOutline,
} from "react-icons/io5";
import arService from "../../../../../services/arService";
import { hasPermission } from "../../../../../utils/permission";
import "./CreditNoteList.css";
import { showMessageBox } from '../../../../site/fn';
import { EmptyState, ErrorState } from '../../../../../shared-ui/States';

const CreditNoteList = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const canEdit = hasPermission("ar-credit-note", "set");
  const canDelete = hasPermission("ar-credit-note", "delete");
  const canDeleteDocument = (doc) =>
    doc.approval_status !== "Approved" && !doc.is_deleted;
  const canApprove = hasPermission("ar-credit-note", "approve");

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹ 0.00";
    return `₹ ${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusBadge = (status, approvalStatus, isDraft) => {
    if (isDraft) {
      return <span className="status-badge draft">Draft</span>;
    }
    if (approvalStatus === "Approved") {
      return <span className="status-badge approved">Approved</span>;
    }
    if (approvalStatus === "Rejected") {
      return <span className="status-badge rejected">Rejected</span>;
    }
    if (approvalStatus === "Pending Approval" || approvalStatus === "Pending") {
      return <span className="status-badge pending">Pending Approval</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  useEffect(() => {
    fetchCreditNotes();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredData(creditNotes);
    } else {
      const filtered = creditNotes.filter((cn) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (cn.doc_no && cn.doc_no.toLowerCase().includes(searchLower)) ||
          (cn.customer_name && cn.customer_name.toLowerCase().includes(searchLower)) ||
          (cn.site_name && cn.site_name.toLowerCase().includes(searchLower)) ||
          (cn.reason && cn.reason.toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, creditNotes]);

  const fetchCreditNotes = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const data = await arService.getCreditNotes();
      const sorted = [...data].sort((a, b) => {
        const idDiff = Number(b.id || 0) - Number(a.id || 0);
        if (idDiff !== 0) return idDiff;
        return new Date(b.credit_note_date || 0) - new Date(a.credit_note_date || 0);
      });
      setCreditNotes(sorted);
      setFilteredData(sorted);
    } catch (error) {
      console.error("Error fetching credit notes:", error);
      setLoadError(error.message || "Failed to fetch credit notes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (id) => {
    navigate(`/ar/credit-notes/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/ar/credit-notes/${id}/edit`);
  };

  const handleDelete = async (id, docNo) => {
    const reason = window.prompt(
      `⚠️ Delete Credit Note ${docNo}\n\nThis action will permanently block this document number.\nPlease provide a reason for deletion:`
    );
    
    if (!reason || reason.trim() === "") {
      if (reason !== null) {
        showMessageBox("Deletion reason is required.", "error");
      }
      return;
    }

    if (!window.confirm(`Are you sure you want to delete Credit Note ${docNo}?\n\nReason: ${reason}`)) {
      return;
    }

    try {
      await arService.deleteCreditNote(id, reason.trim());
      showMessageBox("Credit Note deleted successfully!", "success");
      fetchCreditNotes();
    } catch (error) {
      console.error("Error deleting credit note:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to delete credit note.";
      showMessageBox(`Error: ${errorMessage}`, "error");
    }
  };

  const handleApprove = async (id, docNo) => {
    if (!window.confirm(`Are you sure you want to approve Credit Note ${docNo}?`)) {
      return;
    }

    try {
      await arService.approveCreditNote(id);
      showMessageBox("Credit Note approved successfully!", "success");
      fetchCreditNotes();
    } catch (error) {
      console.error("Error approving credit note:", error);
      showMessageBox("Failed to approve credit note. Please try again.", "error");
    }
  };

  const handleReject = async (id, docNo) => {
    const reason = window.prompt(`Enter reason for rejecting Credit Note ${docNo}:`);
    if (!reason) return;

    try {
      await arService.rejectCreditNote(id, reason);
      showMessageBox("Credit Note rejected successfully!", "success");
      fetchCreditNotes();
    } catch (error) {
      console.error("Error rejecting credit note:", error);
      showMessageBox("Failed to reject credit note. Please try again.", "error");
    }
  };

  const handleSendForApproval = async (id) => {
    try {
      await arService.sendCreditNoteForApproval(id);
      showMessageBox("Credit Note sent for approval successfully!", "success");
      fetchCreditNotes(); // Refresh list
    } catch (error) {
      console.error("Error sending credit note for approval:", error);
      showMessageBox(error.message || "Failed to send for approval. Please ensure an approver is selected.", "error");
    }
  };

  const handlePrint = (id) => {
    navigate(`/ar/credit-notes/${id}/print`);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading credit notes...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="credit-note-list-container">
        <ErrorState
          title="Could not load credit notes"
          description={loadError}
          onRetry={fetchCreditNotes}
        />
      </div>
    );
  }

  return (
    <div className="credit-note-list-container">
      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Doc No, Customer, Site, or Reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="list-info">
          <span>Total: {filteredData.length} credit notes</span>
        </div>
      </div>

      <div className="table-container">
        {filteredData.length === 0 ? (
          <EmptyState
            title="No credit notes"
            description={
              searchTerm
                ? "No credit notes found matching your search."
                : "Credit notes will appear here once they are created."
            }
          />
        ) : (
        <table className="credit-note-table">
          <thead>
            <tr>
              <th>Doc No</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Reason</th>
              <th className="text-right">Amount</th>
              <th>Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
              {filteredData.map((cn) => (
                <tr key={cn.id}>
                  <td>
                    <strong>{cn.doc_no || "N/A"}</strong>
                  </td>
                  <td>{formatDate(cn.credit_note_date)}</td>
                  <td>{cn.customer_name || "N/A"}</td>
                  <td className="reason-cell">{cn.reason || "N/A"}</td>
                  <td className="text-right">{formatCurrency(cn.total_amount)}</td>
                  <td>{getStatusBadge(cn.status, cn.approval_status, cn.is_draft)}</td>
                  <td className="text-center">
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn-action btn-view"
                        onClick={() => handleView(cn.id)}
                        title="View Details"
                      >
                        <IoEyeOutline size={16} />
                      </button>
                      {canEdit && (cn.is_draft || cn.approval_status === "Rejected") && (
                        <>
                          <button
                            type="button"
                            className="btn-action btn-edit"
                            onClick={() => handleEdit(cn.id)}
                            title="Edit"
                          >
                            <IoCreateOutline size={16} />
                          </button>
                          {cn.is_draft && (
                            <button
                              type="button"
                              className="btn-action btn-send"
                              onClick={() => handleSendForApproval(cn.id)}
                              title="Send for Approval"
                            >
                              <IoSendOutline size={16} />
                            </button>
                          )}
                        </>
                      )}
                      {canApprove &&
                        (cn.approval_status === "Pending" ||
                          cn.approval_status === "Pending Approval") && (
                        <>
                          <button
                            type="button"
                            className="btn-action btn-approve"
                            onClick={() => handleApprove(cn.id, cn.doc_no)}
                            title="Approve"
                          >
                            <IoCheckmarkOutline size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn-action btn-reject"
                            onClick={() => handleReject(cn.id, cn.doc_no)}
                            title="Reject"
                          >
                            <IoCloseOutline size={16} />
                          </button>
                        </>
                      )}
                      {cn.approval_status === "Approved" && (
                        <button
                          type="button"
                          className="btn-action btn-print"
                          onClick={() => handlePrint(cn.id)}
                          title="Print PDF"
                        >
                          <IoPrintOutline size={16} />
                        </button>
                      )}
                      {canDelete && canDeleteDocument(cn) && (
                        <button
                          type="button"
                          className="btn-action btn-delete"
                          onClick={() => handleDelete(cn.id, cn.doc_no)}
                          title="Delete"
                        >
                          <IoTrashOutline size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
};

export default CreditNoteList;
